// Shared formatting helpers.
// Address columns are TEXT in D1; we store a JSON object as a stringified
// payload. parseAddress / stringifyAddress are the only places the
// JSON-vs-legacy-string distinction lives.

const ADDRESS_FIELDS = ['line1', 'line2', 'city', 'province', 'postCode', 'country'];

export function emptyAddress() {
  return { line1: '', line2: '', city: '', province: '', postCode: '', country: '' };
}

// Returns true when every address field is missing/blank.
export function isAddressEmpty(address) {
  if (!address || typeof address !== 'object') return true;
  return ADDRESS_FIELDS.every((key) => {
    const v = address[key];
    return typeof v !== 'string' || v.trim() === '';
  });
}

// Coerce whatever the API returned into the canonical 6-field address shape.
// - JSON object string  → parsed object (filled with empty strings for missing keys)
// - Plain non-JSON text → put it all in line1 (legacy data)
// - null / undefined / empty → emptyAddress()
export function parseAddress(value) {
  if (value == null) return emptyAddress();

  // Already an object (e.g. caller passed an in-memory address).
  if (typeof value === 'object' && !Array.isArray(value)) {
    const base = emptyAddress();
    for (const key of ADDRESS_FIELDS) {
      if (typeof value[key] === 'string') base[key] = value[key];
    }
    return base;
  }

  if (typeof value !== 'string') return emptyAddress();
  const trimmed = value.trim();
  if (trimmed === '') return emptyAddress();

  // JSON-encoded object → use as-is. Anything else (array, number, malformed)
  // falls back to the legacy single-line interpretation.
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const base = emptyAddress();
        for (const key of ADDRESS_FIELDS) {
          if (typeof parsed[key] === 'string') base[key] = parsed[key];
        }
        return base;
      }
    } catch {
      // Fall through to legacy treatment.
    }
  }

  // Legacy plain-text address — preserve the original in line1 so nothing is lost.
  return { ...emptyAddress(), line1: value };
}

// Stringify for storage. Returns null when the address is fully blank so the
// API can persist a NULL instead of "{}". Trims each field before serialising.
export function stringifyAddress(address) {
  if (!address || typeof address !== 'object') return null;
  const trimmed = {};
  for (const key of ADDRESS_FIELDS) {
    const v = address[key];
    trimmed[key] = typeof v === 'string' ? v.trim() : '';
  }
  if (ADDRESS_FIELDS.every((key) => trimmed[key] === '')) return null;
  return JSON.stringify(trimmed);
}
